import { Component, OnInit, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest } from 'rxjs';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { DeploymentClass } from 'src/app/models/instance-class';
import { RouterSegments } from 'src/app/models/router-segments';
import { SolutionVersionsInfo } from 'src/app/models/service/solution-versions-info';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { InstancesService } from 'src/app/services/api/instances.service';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { TenantService } from 'src/app/services/api/tenant.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { SolutionVersionsService } from 'src/app/services/util/solution-versions.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { Permissions } from 'src/app/models/principal/permission';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import { PermissionKeys } from 'src/app/models/principal/permission-keys';

const REQUIRED_PERMISSIONS = [
  { 
    key: PermissionKeys.BLUEPRINT_READ,
    action: Permissions.BLUEPRINT_READ
  }
];

@Component({
    selector: 'app-tenant-disaster-recovery',
    templateUrl: './tenant-disaster-recovery.component.html',
    styleUrls: ['./tenant-disaster-recovery.component.scss'],
    standalone: false
})
export class TenantDisasterRecoveryComponent  implements OnInit {
  private destroyRef = inject(DestroyRef);

  PermissionKeys = PermissionKeys;
  permissionsMap: { [key: string]: boolean };
  tenantId: string;
  serviceDefinitionList: any;
  solution: any;
  tenant: any;
  isAMR: boolean;
  dataLoaded = false;
  instances: any;
  isLive: boolean;
  serviceVersionsInfo: SolutionVersionsInfo;
  solutionVersions: any[];
  latestReleaseVersion: any;
  initialLoader = true;
  pageSize: number;
  pageNumber = 1;
  showPagination = true;
  pageIndex = 0;
  paginationSize = 10;
  filterTenant: any;
  searchText: any;
  blueprint: any;

  private readonly solutionName = ['Tenant Shared Ingress', 'Tenant Shared Services'];
  private readonly environmentName = ['TSS', 'TSI'];
  
  readonly INSTANCE_DEPLOYED_STATES = [DeployStatus.DEPLOYED, DeployStatus.CONFIGURING,
    DeployStatus.CONFIGURATION_FAILED, DeployStatus.CONFIGURED, DeployStatus.UPDATING, DeployStatus.UPDATED, DeployStatus.TESTED, DeployStatus.TEST_FAILED];

  constructor(private activatedRoute: ActivatedRoute, private blueprintsService: BlueprintsService, private solutionsService: SolutionsService,
    private solutionVersionsService: SolutionVersionsService, private tenantService: TenantService, private instanceService: InstancesService,
    private router: Router, private authService: AuthService, private sidenavService: SidenavService, private toastrService: CustomToastrService, private cdr: ChangeDetectorRef,
    private permissionsService: PermissionsService) {
    
  }

  ngOnInit(): void {
    this.sidenavService.tenantSelectedMenu = 'TENANT_LAYOUT.MENU.DISASTER_RECOVERY';
    const tenant = this.authService.principal.provider;
    this.permissionsMap = this.permissionsService.resolveAPIPermissions(REQUIRED_PERMISSIONS);
    if (!this.permissionsMap[PermissionKeys.BLUEPRINT_READ]) {
      this.router.navigate(['/', tenant, 'tenant', 'permission-unauthorized'], {skipLocationChange: true});
    }
    this.tenantId = this.activatedRoute.snapshot.params.id;
    this.getBlueprints();
    this.getTenants(this.tenantId);
  }

  ngAfterViewChecked(){
    this.cdr.detectChanges();
  }

  /* Environment (e.g. "TSS", "TSI") that should be hidden from the DR listing.
   Matches on exact name only, to avoid false positives with names like "TSIngress11". */
  private isRestrictedBlueprintName(name: string): boolean {
    return this.environmentName.includes((name || '').trim());
  }

  /* Solution (e.g. "Tenant Shared Services", "Tenant Shared Ingress")
   Matches on prefix only, to avoid unrelated solutions (e.g. "Tenant Shared Infrstructure") */
  private isRestrictedSolutionName(name: string): boolean {
    return this.solutionName.some(sol => (name || '').startsWith(sol));
  }

  getBlueprints(): void {
    this.pageIndex = 0;
    this.filterTenant = [];
    this.blueprintsService.getBlueprints()
    .pipe(takeUntilDestroyed(this.destroyRef))
    .subscribe((result: any) => {
      result = result.sort((a,b) => b?.dateCreated?.localeCompare(a?.dateCreated));
      this.initialLoader = false;
      this.filterTenant = result.filter(item => item.tenantId == this.tenantId && !this.isRestrictedBlueprintName(item?.name));
      this.blueprint = this.filterTenant;
      this.serviceDefinitionList = this.filterTenant?.slice(this.pageIndex, this.paginationSize);
      if(this.serviceDefinitionList.length < 10) {
      this.loadData();
      } 
    }, (error) => {
        setTimeout(() => {
		      const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
  }

  loadData(): void {
    this.serviceDefinitionList.forEach( blueprint => {
      const obj = {
        blueprintId: blueprint.id,
        tenantId: this.tenantId
      };
      combineLatest([
        this.solutionsService.getSolutionVersions(blueprint.solutionId),
        this.instanceService.getInstances(obj)
      ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([solutionVersion, instances]) => {

        this.solutionVersions = solutionVersion;
        this.instances = instances?.data;
        this.isLive = this.resolveLiveStatus(this.instances);
        this.serviceVersionsInfo = this.solutionVersionsService.getSolutionVersionsInfo(solutionVersion, this.solutionVersions, this.instances);
        const liveInstance = this.instances.find(instance => instance.deploymentClass === DeploymentClass.A);

        if (liveInstance) {
          this.latestReleaseVersion = this.solutionVersions.filter(version =>
            version.id === liveInstance.solutionVersionId)[0]?.releaseVersion;
        } else {
          const instanceLength = this.instances.length;
          const latestInstanceSolutionVersion = this.instances[instanceLength - 1]?.solutionVersionId;
          this.latestReleaseVersion = this.solutionVersions.filter(version =>
            version.id === latestInstanceSolutionVersion)[0]?.releaseVersion;
        }
        let releaseVersionText;
        if (this.latestReleaseVersion) {
          releaseVersionText = this.latestReleaseVersion;
        } else {
          releaseVersionText = this.serviceVersionsInfo?.currentVersion?.releaseVersion;
        }
        this.isAMR = releaseVersionText && this.solutionVersionsService.releaseVersion(releaseVersionText) ? true : false;
        if (this.instances?.length) {
          const latestSolutionVersionId = this.instances[this.instances.length - 1]?.solutionVersionId;
          const solutions = solutionVersion.filter(version => version.id === latestSolutionVersionId);
          blueprint.solutionName = solutions[solutions?.length -1]?.name;
        } else {
          blueprint.solutionName = solutionVersion[0]?.name;
        }        
        if (this.isRestrictedSolutionName(blueprint.solutionName)) {
          this.removeBlueprintFromLists(blueprint);
          this.dataLoaded = true;
          return;
        }
        blueprint.solutionName = blueprint.solutionName ? blueprint.solutionName : 'N/A';
        blueprint.version = releaseVersionText;
        blueprint.isAMR = this.isAMR;
        this.dataLoaded = true;
      },
      (error) => {
        blueprint.solutionName = blueprint.solutionName ? blueprint.solutionName : 'N/A';
      });
    });
    if (this.serviceDefinitionList.length === 0) {
      this.dataLoaded = true;
    }
  }

  /* Removes a restricted blueprint (e.g. TSS/TSI shared-services environment)
   from all local lists used for display and pagination */
  private removeBlueprintFromLists(blueprint: any): void {
    this.serviceDefinitionList = this.serviceDefinitionList?.filter(b => b !== blueprint);
    this.filterTenant = this.filterTenant?.filter(b => b !== blueprint);
    this.blueprint = this.blueprint?.filter(b => b !== blueprint);
  }

  searchBlueprint(event?): void {
    if (event) {
      this.searchText = event.target.value;
    } else if (this.searchText) {
      this.searchText = this.searchText;
    }

    this.blueprint = this.filterTenant;
    if (this.searchText) {
      this.blueprint = this.blueprint.filter(item => item?.name?.toLocaleLowerCase().includes(this.searchText.toLocaleLowerCase()));
    } else {
      this.blueprint = this.filterTenant;
    }
    this.pageIndex = 0;
    this.serviceDefinitionList = this.blueprint?.slice(this.pageIndex, this.paginationSize);
    this.loadData();
  }

  getTenants(tenantId): void {
    this.tenantService.getTenants(tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        this.tenant = data;
        if (this.tenant[0]?.parameters?.hasSecondaryRegion && this.tenant[0]?.parameters?.hasSecondaryRegion === 'false') {
           this.router.navigate(['/', this.tenantId, 'tenant', 'permission-unauthorized'], {skipLocationChange: true});
        }
    });
  }

  cardClick(service) {
    this.router.navigate(['/', this.authService.principal.provider, RouterSegments.TENANT , this.tenantId, RouterSegments.DISASTER_RECOVERY, service.id]);
  }

  pageChanged(event): any {
    if (event.length) {
      this.pageIndex = event.pageIndex;
      this.paginationSize = event.pageSize;
      const firstIndex = this.pageIndex * this.paginationSize;
      this.serviceDefinitionList = this.blueprint.slice(firstIndex, firstIndex + this.paginationSize);
      this.loadData();
    }
  }

  private resolveLiveStatus(instances: any[]): boolean {
    const live = instances.find(instance => instance.deploymentClass === DeploymentClass.A);
    return this.INSTANCE_DEPLOYED_STATES.includes(live?.status);
  }

}
